import {
  PHOTO_EFFECT_DEFINITIONS,
  type PhotoEffectKind,
  getPhotoEffectDefinition,
} from '@/lib/photo-effects/photo-effect-defs'
import type { PhotoEffectDefinition } from '@/lib/photo-effects/types/photo-effect-definition'
import type { PhotoEffectId } from '@/lib/photo-effects/types/photo-effect-id'
import type { PhotoEffectsState } from '@/lib/photo-effects/types/photo-effects-state'

function resolvePhotoEffectDefinition(
  id: PhotoEffectId,
  definitions: readonly PhotoEffectDefinition[] = PHOTO_EFFECT_DEFINITIONS
): PhotoEffectDefinition {
  const fromList = definitions.find((d) => d.id === id)

  if (fromList) return fromList

  return getPhotoEffectDefinition(id)
}

/** Build the default UI state for every catalog effect (all inactive, adjustable at defaults). */
export function createDefaultPhotoEffectsState(
  definitions: readonly PhotoEffectDefinition[] = PHOTO_EFFECT_DEFINITIONS
): PhotoEffectsState {
  const state = {} as PhotoEffectsState

  for (const def of definitions) {
    if (def.kind === 'binary') {
      state[def.id] = { active: false }
    } else {
      state[def.id] = { active: false, value: def.defaultValue ?? 0 }
    }
  }

  return state
}

/**
 * Clamp an adjustable effect value to its catalog range.
 * Symmetric: -10…10. Percent: 1…100 (0 and negatives clamp to 1).
 * Binary ids return the input unchanged.
 */
export function clampPhotoEffectValue(
  id: PhotoEffectId,
  value: number,
  definitions: readonly PhotoEffectDefinition[] = PHOTO_EFFECT_DEFINITIONS
): number {
  const def = resolvePhotoEffectDefinition(id, definitions)

  if (def.kind === 'binary' || def.min == null || def.max == null) {
    return value
  }

  if (!Number.isFinite(value)) {
    return def.defaultValue ?? def.min
  }

  const clamped = Math.min(def.max, Math.max(def.min, value))

  // Sepia slider/input: whole UI 1…100 (WMPCM-9477 / WMPCM-12096); POST `v` uses backend map separately.
  if (id === 'sepia') return Math.round(clamped)

  return clamped
}

/** Count of effects currently toggled on (Clear all (n) semantics — active eyeballs). */
export function countActivePhotoEffects(
  state: PhotoEffectsState,
  definitions: readonly PhotoEffectDefinition[] = PHOTO_EFFECT_DEFINITIONS
): number {
  let count = 0

  for (const def of definitions) {
    if (state[def.id]?.active) count += 1
  }

  return count
}

/** Reset every effect to inactive + catalog defaults (Clear all). */
export function clearAllPhotoEffects(
  state: PhotoEffectsState,
  definitions: readonly PhotoEffectDefinition[] = PHOTO_EFFECT_DEFINITIONS
): PhotoEffectsState {
  void state

  return createDefaultPhotoEffectsState(definitions)
}

/**
 * Toggle an effect active flag (WMPCM-9474).
 * Newly activated ids are prepended to `activationOrder` (most-recent active first).
 */
/** Set an adjustable effect value (WMPCM-9477). Does not change the active flag. */
export function withPhotoEffectValue(
  state: PhotoEffectsState,
  id: PhotoEffectId,
  value: number,
  definitions: readonly PhotoEffectDefinition[] = PHOTO_EFFECT_DEFINITIONS
): PhotoEffectsState {
  const entry = state[id]

  return {
    ...state,
    [id]: { ...entry, value: clampPhotoEffectValue(id, value, definitions) },
  }
}

export function withToggledPhotoEffect(
  state: PhotoEffectsState,
  activationOrder: readonly PhotoEffectId[],
  id: PhotoEffectId
): { state: PhotoEffectsState; activationOrder: PhotoEffectId[] } {
  const entry = state[id]
  const nextActive = !entry.active
  const nextState: PhotoEffectsState = {
    ...state,
    [id]: { ...entry, active: nextActive },
  }
  const withoutId = activationOrder.filter((x) => x !== id)
  const def = resolvePhotoEffectDefinition(id)

  // Adjustable effects stay in activation order when toggled off so POST can send `disable: true`.
  const nextOrder = nextActive
    ? [id, ...withoutId]
    : def.kind === 'binary'
      ? withoutId
      : [...withoutId, id]

  return {
    state: nextState,
    activationOrder: nextOrder,
  }
}

/**
 * Display order for the Adjust list — stable Figma/catalog order (WMPCM-11629).
 * `activationOrder` is still maintained for POST/disable semantics; toggling does not reorder rows.
 */
export function orderPhotoEffectsForDisplay(
  kinds: readonly PhotoEffectKind[] = ['binary'],
  definitions: readonly PhotoEffectDefinition[] = PHOTO_EFFECT_DEFINITIONS
): PhotoEffectDefinition[] {
  const kindSet = new Set(kinds)

  return definitions.filter((d) => kindSet.has(d.kind))
}
